import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button, Text, Field, Input, Textarea,
  makeStyles, mergeClasses, tokens,
  MessageBar, MessageBarBody,
} from '@fluentui/react-components'
import { ArrowLeft24Regular } from '@fluentui/react-icons'
import { absenceRequestsService, AbsenceType, ABSENCE_TYPES, DayHalf } from '../../services/absenceRequestsService'
import { meService } from '../../services/meService'
import VacationBalanceSummary from '../../components/VacationBalanceSummary'
import { NativeDateInput } from '../../components/NativeDateInput'

// ── Konfigurace prominentních typů ───────────────────────────────────────────

const FEATURED_ROW1 = ['VACATION', 'SICK']
const FEATURED_ROW2 = ['PAID_SPECIAL', 'OCR']

const FEATURED_EMOJI: Record<string, string> = {
  VACATION:    '☀️',
  SICK:        '🤒',
  PAID_SPECIAL:'🩺',
  OCR:         '👶',
}

const FEATURED_TIP: Record<string, string> = {
  PAID_SPECIAL: 'svatba · doktor · doprovod · krev',
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function parseApiError(err: any): string {
  const status = err?.response?.status
  if (status === 403) return 'Nemáte oprávnění k této operaci.'
  if (status === 409 && typeof err?.response?.data === 'string') return err.response.data
  if (err?.response?.data?.errors)
    return Object.values(err.response.data.errors).flat().join(', ')
  if (typeof err?.response?.data === 'string' && err.response.data.length < 1000)
    return err.response.data
  return 'Nastala chyba při ukládání.'
}

// ── Styles ────────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  page: {
    maxWidth: '480px',
    margin: '0 auto',
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingBottom: '88px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    paddingTop: '8px',
    paddingBottom: '16px',
  },
  sectionLabel: {
    display: 'block',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    color: tokens.colorNeutralForeground3,
    marginBottom: '8px',
  },
  section: {
    marginBottom: '20px',
  },

  // Prominentní karta (Dovolená, Nemoc, Placené volno, OČR)
  featCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '14px 8px 12px 8px',
    border: `2px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground1,
    userSelect: 'none' as const,
    minHeight: '88px',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  featCardSelected: {
    border: `2px solid ${tokens.colorBrandForeground1}`,
    backgroundColor: tokens.colorBrandBackground2,
  },

  // Sekundární karta (ostatní typy) — kompaktní, bez emoji
  secCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 10px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground1,
    userSelect: 'none' as const,
    minHeight: '38px',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  secCardSelected: {
    border: `1px solid ${tokens.colorBrandForeground1}`,
    backgroundColor: tokens.colorBrandBackground2,
  },

  datesRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  hint: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
  },
  footer: {
    position: 'fixed' as const,
    bottom: '0',
    left: '0',
    right: '0',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: '12px 16px',
    display: 'flex',
    gap: '8px',
    zIndex: 100,
  },
})

// ── Komponenta ────────────────────────────────────────────────────────────────

export default function NewAbsenceRequest() {
  const styles = useStyles()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [typeId, setTypeId] = useState<number | null>(null)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [totalDays, setTotalDays] = useState(0)
  const [daysEdited, setDaysEdited] = useState(false)
  const [dayHalf, setDayHalf] = useState<DayHalf>(DayHalf.Full)
  const [location, setLocation] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState('')

  const { data: myProfile } = useQuery({ queryKey: ['me'], queryFn: meService.getMe })
  const types = ABSENCE_TYPES

  // Počet pracovních dnů ze serveru (respektuje DB svátky a rozvrh zaměstnance)
  const { data: serverDays, isFetching: daysLoading } = useQuery({
    queryKey: ['working-days', startDate, endDate, myProfile?.id],
    queryFn: () => absenceRequestsService.getWorkingDays(startDate, endDate, myProfile!.id!),
    enabled: !!startDate && !!endDate && !!myProfile?.id && !daysEdited,
    staleTime: 0,
  })

  const selectedType = types.find(t => t.id === typeId)
  const isVacation = selectedType?.code === 'VACATION'

  // Skupiny typů
  const row1     = types.filter(t => FEATURED_ROW1.includes(t.code))
  const row2     = types.filter(t => FEATURED_ROW2.includes(t.code))
  const secondary = types.filter(t =>
    !FEATURED_ROW1.includes(t.code) && !FEATURED_ROW2.includes(t.code)
  )
  // Seřadit row1/row2 dle pořadí v konstantách
  row1.sort((a, b) => FEATURED_ROW1.indexOf(a.code) - FEATURED_ROW1.indexOf(b.code))
  row2.sort((a, b) => FEATURED_ROW2.indexOf(a.code) - FEATURED_ROW2.indexOf(b.code))

  // Default na Dovolená po načtení typů
  useEffect(() => {
    if (types.length > 0 && typeId === null) {
      const dov = types.find(t => t.code === 'VACATION') ?? types[0]
      setTypeId(dov.id)
    }
  }, [types, typeId])

  // Nastavení počtu dnů ze serveru
  useEffect(() => {
    if (serverDays != null && !daysEdited) {
      setTotalDays(serverDays > 0 ? serverDays : 0)
    }
  }, [serverDays, daysEdited])

  const isSingleDay = !!startDate && !!endDate && startDate === endDate

  // Vícedenní / prázdný rozsah → vždy celý den (žádný půlden) — #11
  useEffect(() => {
    if (!isSingleDay && dayHalf !== DayHalf.Full) {
      setDayHalf(DayHalf.Full)
      setDaysEdited(false)
    }
  }, [isSingleDay]) // eslint-disable-line react-hooks/exhaustive-deps

  const setFullDay = () => { setDayHalf(DayHalf.Full); setDaysEdited(true); setTotalDays(1) }
  const setHalf = (dh: DayHalf) => { setDayHalf(dh); setDaysEdited(true); setTotalDays(0.5) }
  const adjustWholeDays = (delta: number) => {
    setDaysEdited(true)
    setTotalDays(prev => Math.max(1, Math.round(prev + delta)))
  }

  const createMutation = useMutation({
    mutationFn: () => absenceRequestsService.create({
      employeeId: myProfile?.id ?? 0,
      absenceTypeId: typeId!,
      startDate,
      endDate,
      location,
      note: note || undefined,
      // Pokud uživatel dny ručně neupravil, necháme server přepočítat z DB svátků.
      // Pokud upravil (daysEdited), respektujeme jeho volbu (např. půlden).
      totalDays: daysEdited ? totalDays : undefined,
      dayHalf,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-requests'] })
      navigate('/my-requests')
    },
    onError: (err: any) => setError(parseApiError(err)),
  })

  const handleSubmit = () => {
    setError('')
    if (!typeId)             { setError('Vyberte typ nepřítomnosti.'); return }
    if (!startDate)          { setError('Zadejte datum od.'); return }
    if (!endDate)            { setError('Zadejte datum do.'); return }
    if (endDate < startDate) { setError('Datum do nemůže být dříve než datum od.'); return }
    if (!location.trim())    { setError('Zadejte místo pobytu.'); return }
    if (totalDays <= 0)      { setError('Počet dnů musí být větší než 0.'); return }
    createMutation.mutate()
  }

  // Helper: karta prominentního typu
  const FeatCard = ({ t }: { t: AbsenceType }) => {
    const selected = typeId === t.id
    const tip = FEATURED_TIP[t.code]
    return (
      <div
        className={mergeClasses(styles.featCard, selected && styles.featCardSelected)}
        onClick={() => setTypeId(t.id)}
        role="radio"
        aria-checked={selected}
      >
        <span style={{ fontSize: '32px', lineHeight: '1', marginBottom: '2px' }}>
          {FEATURED_EMOJI[t.code] ?? '📄'}
        </span>
        <Text size={300} weight={selected ? 'semibold' : 'regular'} style={{ textAlign: 'center' }}>
          {t.name}
        </Text>
        {tip && (
          <span style={{
            fontSize: '10px',
            color: selected ? tokens.colorBrandForeground1 : tokens.colorNeutralForeground3,
            textAlign: 'center',
            marginTop: '2px',
            lineHeight: '1.3',
          }}>
            {tip}
          </span>
        )}
      </div>
    )
  }

  // Helper: karta sekundárního typu
  const SecCard = ({ t }: { t: AbsenceType }) => {
    const selected = typeId === t.id
    return (
      <div
        className={mergeClasses(styles.secCard, selected && styles.secCardSelected)}
        onClick={() => setTypeId(t.id)}
        role="radio"
        aria-checked={selected}
      >
        <Text
          size={200}
          weight={selected ? 'semibold' : 'regular'}
          style={{ textAlign: 'center', color: selected ? tokens.colorBrandForeground1 : undefined }}
        >
          {t.name}
        </Text>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      {/* Záhlaví */}
      <div className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft24Regular />}
          onClick={() => navigate('/my-requests')}
        />
        <Text size={500} weight="semibold">Nová žádost</Text>
      </div>

      {/* Banner zůstatku dovolené */}
      {isVacation && (
        <div className={styles.section}>
          <VacationBalanceSummary compact />
        </div>
      )}

      {/* Typ nepřítomnosti */}
      <div className={styles.section}>
        <span className={styles.sectionLabel}>Typ nepřítomnosti</span>

        {/* Řada 1: Dovolená + Nemoc */}
        {row1.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
            {row1.map(t => <FeatCard key={t.id} t={t} />)}
          </div>
        )}

        {/* Řada 2: Placené volno + OČR */}
        {row2.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: secondary.length ? '10px' : '0' }}>
            {row2.map(t => <FeatCard key={t.id} t={t} />)}
          </div>
        )}

        {/* Ostatní — kompaktní, bez emoji */}
        {secondary.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
            {secondary.map(t => <SecCard key={t.id} t={t} />)}
          </div>
        )}
      </div>

      {/* Termín */}
      <div className={styles.section}>
        <span className={styles.sectionLabel}>Termín</span>
        <div className={styles.datesRow}>
          <Field label="Datum od" required>
            <NativeDateInput value={startDate} onChange={setStartDate} required />
          </Field>
          <Field label="Datum do" required>
            <NativeDateInput
              value={endDate}
              onChange={setEndDate}
              min={startDate || undefined}
              required
            />
          </Field>
        </div>
      </div>

      {/* Rozsah / počet dní — #11 */}
      <div className={styles.section}>
        {isSingleDay ? (
          <>
            <span className={styles.sectionLabel}>Rozsah</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button appearance={dayHalf === DayHalf.Full ? 'primary' : 'secondary'} onClick={setFullDay}>Celý den</Button>
              <Button appearance={dayHalf === DayHalf.Morning ? 'primary' : 'secondary'} onClick={() => setHalf(DayHalf.Morning)}>Dopoledne</Button>
              <Button appearance={dayHalf === DayHalf.Afternoon ? 'primary' : 'secondary'} onClick={() => setHalf(DayHalf.Afternoon)}>Odpoledne</Button>
            </div>
          </>
        ) : (
          <>
            <span className={styles.sectionLabel}>Pracovních dnů</span>
            <div style={{
              display: 'flex', alignItems: 'center',
              border: `1px solid ${tokens.colorNeutralStroke1}`,
              borderRadius: tokens.borderRadiusMedium,
              overflow: 'hidden', height: '40px', width: '200px',
            }}>
              <button
                type="button"
                style={{
                  width: '48px', height: '40px', flexShrink: 0,
                  border: 'none', borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
                  background: tokens.colorNeutralBackground2,
                  cursor: totalDays <= 1 ? 'default' : 'pointer',
                  fontSize: '22px', lineHeight: '1',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: totalDays <= 1 ? 0.3 : 1,
                }}
                onClick={() => adjustWholeDays(-1)}
                disabled={totalDays <= 1}
              >
                −
              </button>
              <span style={{
                flex: 1, textAlign: 'center',
                fontSize: '17px', fontWeight: '600',
                color: tokens.colorNeutralForeground1,
              }}>
                {String(totalDays).replace('.', ',')}
              </span>
              <button
                type="button"
                style={{
                  width: '48px', height: '40px', flexShrink: 0,
                  border: 'none', borderLeft: `1px solid ${tokens.colorNeutralStroke1}`,
                  background: tokens.colorNeutralBackground2,
                  cursor: 'pointer',
                  fontSize: '22px', lineHeight: '1',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                onClick={() => adjustWholeDays(1)}
              >
                +
              </button>
            </div>
            <div className={styles.hint}>
              {daysEdited ? 'Ručně upraveno' : daysLoading ? 'Počítám…' : 'Automaticky vypočteno (bez svátků a víkendů)'}
            </div>
          </>
        )}
      </div>

      {/* Místo pobytu */}
      <div className={styles.section}>
        <Field label="Místo pobytu" required>
          <Input
            value={location}
            onChange={e => setLocation(e.target.value)}
          />
        </Field>
      </div>

      {/* Poznámka */}
      <div className={styles.section}>
        <Field label="Poznámka">
          <Textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={2}
          />
        </Field>
      </div>

      {/* Chybová zpráva */}
      {error && (
        <div className={styles.section}>
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        </div>
      )}

      {/* Sticky footer */}
      <div className={styles.footer}>
        <Button
          style={{ flex: 1 }}
          appearance="secondary"
          onClick={() => navigate('/my-requests')}
          disabled={createMutation.isPending}
        >
          Zrušit
        </Button>
        <Button
          style={{ flex: 1 }}
          appearance="primary"
          onClick={handleSubmit}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending ? 'Odesílám...' : 'Podat žádost'}
        </Button>
      </div>
    </div>
  )
}
